import React, { useState } from "react";

import {
  Grid,
  IconButton,
  Tooltip,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Popper,
  Paper,
  InputAdornment
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { TextField } from "@aclymatepackages/atoms";

import InputBlock from "./InputBlock";
import NewVendorForm from "../vendors/NewVendorForm";

import { getVendorDisplayName } from "../../../helpers/components/vendors";
import useEmissionsContext from "../../../helpers/contexts/emissions";

const CustomAutocomplete = ({ label = "", options, value, setValue }) => {
  const [inputValue, setInputValue] = useState(
    getVendorDisplayName(value) || ""
  );
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleInputChange = (event) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);
    setFilteredOptions(
      options.filter((vendor) => {
        const { name: canonicalName = "", nickname = "" } = vendor;
        const displayName = getVendorDisplayName(vendor);
        const searchText = newInputValue.toLowerCase();

        return (
          displayName.toLowerCase().includes(searchText) ||
          canonicalName.toLowerCase().includes(searchText) ||
          nickname.toLowerCase().includes(searchText)
        );
      })
    );
    return setAnchorEl(event.currentTarget);
  };

  const handleOptionSelect = (option) => {
    setInputValue(getVendorDisplayName(option));
    setValue(option);
    return setAnchorEl(null);
  };

  return (
    <>
      <TextField
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        fullWidth
        variant="outlined"
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setInputValue("");
                  setValue(null);
                }}
              >
                <CancelIcon color="white" />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <Popper
        placement="bottom-start"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        style={{ maxHeight: "300px", zIndex: 1300 }}
      >
        <Paper style={{ maxHeight: "300px", overflowY: "auto" }}>
          <List dense>
            {filteredOptions.map((option, index) => (
              <ListItem key={index}>
                <ListItemButton onClick={() => handleOptionSelect(option)}>
                  <ListItemText primary={getVendorDisplayName(option)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popper>
    </>
  );
};

const VendorsInputStates = ({
  selectableVendors,
  showNewVendorForm,
  setShowNewVendorForm,
  vendor,
  setVendor,
  transactionSubcategory
}) => {
  if (showNewVendorForm) {
    return (
      <NewVendorForm
        setVendor={setVendor}
        setShowNewVendorForm={setShowNewVendorForm}
        transactionSubcategory={transactionSubcategory}
      />
    );
  }

  if (!selectableVendors.length) {
    return (
      <Grid container direction="column" alignItems="center">
        <Grid item>
          <Typography variant="subtitle1" align="center">
            You currently don't have any vendors. Please add a vendor.
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowNewVendorForm(true)}
          >
            Add a vendor
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      wrap="nowrap"
    >
      <Grid item xs={10}>
        <CustomAutocomplete
          label="Available Vendors"
          value={vendor}
          setValue={setVendor}
          options={selectableVendors}
        />
      </Grid>
      <Grid item>
        <Tooltip title="Add a vendor">
          <IconButton
            onClick={() => {
              setVendor(null);
              return setShowNewVendorForm(true);
            }}
          >
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

const VendorsInput = ({
  vendor,
  setVendor,
  transactionSubcategory,
  ...otherProps
}) => {
  const { vendors } = useEmissionsContext();

  const [showNewVendorForm, setShowNewVendorForm] = useState(false);

  return (
    <InputBlock
      type="vendors"
      title={
        showNewVendorForm
          ? `Add a new ${transactionSubcategory} vendor`
          : "Tag a vendor for this transaction"
      }
      cardData={vendor}
      {...otherProps}
    >
      <VendorsInputStates
        selectableVendors={vendors}
        showNewVendorForm={showNewVendorForm}
        setShowNewVendorForm={setShowNewVendorForm}
        vendor={vendor}
        setVendor={setVendor}
        transactionSubcategory={transactionSubcategory}
      />
    </InputBlock>
  );
};
export default VendorsInput;
